'use client';

import { toastMessages } from '@/constants/toast';
import { AnalyticsEvent } from '@/enums/analytics-type';
import { useAddFavoriteTheater, useFavoriteTheaters, useRemoveFavoriteTheater } from '@/hooks/profile/favorite-theaters';
import { Theater } from '@cinemark/content-api-client';
import { ProfileTheater } from '@cinemark/profile-api-client';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CinemarkLink } from '../../cinemark-link/cinemark-link';
import { Button } from '../../ui/button';
import { FavoriteTheaterIconComponent } from '../favorite-theater-icon/favorite-theater-icon';

type TheaterTitleComponentProps = {
  dataLinkCategory: string;
  isHeader?: boolean;
  theater: Theater;
};

export const TheaterTitleComponent = ({ dataLinkCategory, isHeader, theater }: TheaterTitleComponentProps): React.JSX.Element => {
  const { data: favoriteTheaters, isLoading } = useFavoriteTheaters();
  const { mutateAsync: addFavoriteTheater } = useAddFavoriteTheater();
  const { mutateAsync: removeFavoriteTheater } = useRemoveFavoriteTheater();
  const [favoriteTheater, setFavoriteTheater] = useState<ProfileTheater>({ favorite: false, theaterId: theater.theaterId } as ProfileTheater);

  useEffect(() => {
    if (!isLoading && favoriteTheaters?.data) {
      const favoriteTheater = favoriteTheaters.data.find(favoriteTheater => favoriteTheater.theaterId === theater.theaterId);
      if (favoriteTheater) {
        setFavoriteTheater(favoriteTheater);
      }
    }
  }, [favoriteTheaters?.data, isLoading, theater.theaterId]);

  const handleAdd = async (): Promise<void> => {
    setFavoriteTheater(prevValue => ({ ...prevValue, favorite: true }));

    try {
      const result = await addFavoriteTheater({
        analyticsEvent: AnalyticsEvent.FAVORITE_THEATER_ADD,
        theaterName: theater.title,
        updateFavoriteTheater: {
          isFavorite: true,
          theaterId: theater.theaterId || favoriteTheater.theaterId,
        },
      });

      setFavoriteTheater(result);
      toast(toastMessages.theatreAddedToFavorites);
    } catch {
      setFavoriteTheater(prevValue => ({ ...prevValue, favorite: false }));
      toast(toastMessages.unableToMarkTheatreAsFavorite);
    }
  };

  const handleRemove = async (): Promise<void> => {
    setFavoriteTheater(prevValue => ({ ...prevValue, favorite: false }));

    try {
      await removeFavoriteTheater({
        analyticsEvent: AnalyticsEvent.FAVORITE_THEATER_REMOVE,
        theaterName: theater.title,
        updateFavoriteTheater: {
          customerTheaterXrefId: favoriteTheater.customerTheaterXrefId,
          isFavorite: false,
          theaterId: theater.theaterId || favoriteTheater.theaterId,
        },
      });

      toast('Theatre Removed from Favorites', {
        action:
          <Button data-test="button-undo-remove-favorite-theater"
            variant="link"
            onClick={handleAdd}
          >
            Undo
          </Button>,
      });
    } catch {
      setFavoriteTheater(prevValue => ({ ...prevValue, favorite: true }));
      toast('Unable to remove the theatre as favorite.');
    }
  };

  const toggle = async (): Promise<void> => {
    if (favoriteTheater.favorite) {
      await handleRemove();
    } else {
      await handleAdd();
    }
  };

  return (
    <div className="flex gap-xs">
      <FavoriteTheaterIconComponent className="mt-[3px] flex-shrink-0"
        isActive={favoriteTheater.favorite}
        onClick={() => toggle()}
      />

      <CinemarkLink useAnchor
        dataLinkCategory={dataLinkCategory}
        dataLinkName="Theatre Click"
        dataTest={`link-theater-${theater.theaterId}`}
        href={`/theatre-${theater.theaterId}`}
      >
        {isHeader &&
          <h1 className="max-md:h3"
            data-test="theater-name"
          >
            {theater.title}
          </h1>
        }
        {!isHeader &&
          <h3 className="line-clamp-2"
            data-test="theater-name"
          >
            {theater.title}
          </h3>
        }
      </CinemarkLink>
    </div>
  );
};
